import React, { useCallback, useEffect, useMemo, useState } from "react";
import Parse from "parse";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { setSelectedClient } from "../features/cart/cartSlice";
import { useDispatch, useSelector } from "react-redux";

// Own cache key: 'clients' is also written by the Clients page with raw Parse JSON.
const CLIENT_OPTIONS_KEY = 'clientOptions';

// Autocomplete mounts a DOM node per matching option, so an unbounded list makes opening
// the dropdown cost thousands of nodes. Cap what is rendered and let typing narrow it.
const MAX_VISIBLE_OPTIONS = 100;

/**
 * Autocomplete needs a string for every option and for the current value. Clients arrive
 * here in two shapes: the option built below, and the raw ClientDetails JSON stored on a
 * quotation (no label, objectId instead of id). Build the label from the fields when the
 * shape doesn't carry one.
 */
const clientLabel = (client) => {
  if (!client) return '';
  if (typeof client === 'string') return client;
  if (client.label) return client.label;
  const { companyName, firstName, lastName, email, phoneNumber } = client;
  return `${companyName ?? ''} - ${firstName ?? ''} ${lastName ?? ''} - ${email ?? ''} - ${phoneNumber ?? ''}`;
};

const clientId = (client) => client?.id ?? client?.objectId;

// Matches what MUI's default filter does with ignoreAccents, so "Jose" still finds "José".
const normalize = (text) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const SelectClients = (props) => {
  const [clientsData, setClientsData] = useState([]);
  const selectedClientFromStore = useSelector((state) => state.cart.selectedClient);
  const requestForClientsReload = props.requestForClientsReload;
  const setRequestForClientsReload = props.setRequestForClientsReload;
  const dispatch = useDispatch();
  const disabled = props.disabled
  const setFetchingClients = props.setFetchingClients
  const fetchingClients = props.fetchingClients

  useEffect(() => {
    localStorage.removeItem('clients'); // legacy cache, no longer read
    if (localStorage.getItem(CLIENT_OPTIONS_KEY)) {
      setClientsData(JSON.parse(localStorage.getItem(CLIENT_OPTIONS_KEY)));
    } else {
      getQuotationsData();
    };
  }, []);

  useEffect(() => {
    if (requestForClientsReload) {
      getQuotationsData();
    }
  }, [requestForClientsReload]);

  const getQuotationsData = async () => {
    try {
      setFetchingClients?.(true);
      localStorage.removeItem(CLIENT_OPTIONS_KEY);
      const clientsObj = Parse.Object.extend("ClientDetails");
      const query = new Parse.Query(clientsObj).limit(100000);
      const res = await query.find();
      const clients = []
      res.forEach((client) => {
          clients.push({label: clientLabel(client.toJSON()), parseObject: client, email: client.get('email'), id: client.id})
      });
      localStorage.setItem(CLIENT_OPTIONS_KEY, JSON.stringify(clients));
      setClientsData(clients);
      setRequestForClientsReload?.(false);
      setFetchingClients?.(false);
    } catch (error) {
      setFetchingClients?.(false);
      console.log('errpr: ', error);
    }
  };

  // One lowercase haystack per client, computed once per load, so a keystroke is a plain
  // substring scan instead of rebuilding and lowercasing every label again.
  const options = useMemo(
    () => clientsData.map((client) => ({ ...client, search: normalize(clientLabel(client)) })),
    [clientsData]
  );

  const filterOptions = useCallback((clients, { inputValue }) => {
    const terms = normalize(inputValue.trim()).split(/\s+/).filter(Boolean);
    const matches = [];
    for (let i = 0; i < clients.length && matches.length < MAX_VISIBLE_OPTIONS; i++) {
      const haystack = clients[i].search ?? normalize(clientLabel(clients[i]));
      if (terms.every((term) => haystack.includes(term))) {
        matches.push(clients[i]);
      }
    }
    return matches;
  }, []);

  return (
    <Autocomplete
      disablePortal
      id="combo-box-demo"
      sx={{ width: 300 }}
      options={options}
      getOptionLabel={clientLabel}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => {
        const optionId = clientId(option);
        return optionId !== undefined && optionId === clientId(value);
      }}
      renderOption={(optionProps, option) => (
        // Plain li: a styled Box per row costs a style computation on every option.
        <li {...optionProps} key={clientId(option) ?? optionProps.id}>
          {clientLabel(option)}
        </li>
      )}
      disabled={disabled || fetchingClients}
      value={selectedClientFromStore}
      onChange={(event, newValue) => {dispatch(setSelectedClient(newValue)); }}
      renderInput={(params) => <TextField className="primary-color" {...params} label="email" />}
    />
  );
};

export default SelectClients;
